import { useMemo } from "react";

import type {
  ScreenMembersOnSignupId,
  SignupOptions,
} from "@auth0/auth0-acul-js/signup-id";
import SignupIdInstance from "@auth0/auth0-acul-js/signup-id";

import { executeSafely } from "../utils/helpers/executeSafely";

export const useSignupIdManager = () => {
  const signupIdInstance = useMemo(() => new SignupIdInstance(), []);

  const { transaction, screen, untrusted_data } = signupIdInstance;
  const {
    requiredIdentifiers,
    optionalIdentifiers,
    usernamePolicy,
    alternateConnections,
    isPasskeyEnabled,
  } = transaction;
  const { loginLink, texts, captchaImage } = screen;

  const handleSignupId = async (options: SignupOptions): Promise<void> => {
    const payload: SignupOptions = {
      email: options.email?.trim() || undefined,
      username: options.username?.trim() || undefined,
      phone: options.phone?.trim() || undefined,
    };

    if (screen.isCaptchaAvailable && options.captcha?.trim()) {
      payload.captcha = options.captcha.trim();
    }

    executeSafely(
      `SignupId with options: ${JSON.stringify(payload)}`,
      () => signupIdInstance.signup(payload)
    );
  };

  const handleFederatedSignup = async (connectionName: string) => {
    executeSafely(
      `Federated signup with connection: ${connectionName}`,
      () => signupIdInstance.federatedSignup({ connection: connectionName })
    );
  };

  const handlePickCountryCode = async () => {
    executeSafely("Pick country code", () =>
      signupIdInstance.pickCountryCode()
    );
  };

  return {
    signupIdInstance,
    handleSignupId,
    handleFederatedSignup,
    handlePickCountryCode,
    texts: (texts || {}) as ScreenMembersOnSignupId["texts"],
    errors: signupIdInstance.getError(),
    isCaptchaAvailable: screen.isCaptchaAvailable === true,
    captchaImage,
    requiredIdentifiers: requiredIdentifiers ?? [],
    optionalIdentifiers: optionalIdentifiers ?? [],
    usernamePolicy,
    alternateConnections: alternateConnections ?? [],
    isPasskeyEnabled: isPasskeyEnabled === true,
    loginLink: loginLink || null,
    submittedFormData: untrusted_data?.submitted_form_data ?? null,
  };
};

